package com.flowyride.core.api

import kotlinx.serialization.Serializable

/** Authentication and administration endpoints. */
object AccountApi {
    const val REGISTER = "/api/auth/register"
    const val LOGIN = "/api/auth/login"
    const val LOGOUT = "/api/auth/logout"
    const val ME = "/api/auth/me"
    const val PASSWORD = "/api/auth/password"
    const val ADMIN_USERS = "/api/admin/users"

    /** POST, e.g. `/api/admin/users/42/approve`. */
    fun adminAction(userId: Long, action: AdminAction) = "$ADMIN_USERS/$userId/${action.path}"

    /** HttpOnly cookie used by the web app (same origin): the token never reaches JavaScript. */
    const val SESSION_COOKIE = "flowyride_session"
}

enum class AdminAction(val path: String) {
    APPROVE("approve"),
    REJECT("reject"),
    REVOKE("revoke"),
    RESET_PASSWORD("reset-password"),
    MAKE_ADMIN("make-admin"),
    MAKE_USER("make-user"),
}

@Serializable
data class RegisterRequestDto(
    val username: String,
    val email: String,
    val password: String,
    val message: String? = null,
)

@Serializable
data class LoginRequestDto(
    /** Username or email. */
    val login: String,
    val password: String,
    /**
     * Web app: the session lives in an HttpOnly cookie and the response carries no token,
     * so no script can ever read it. Native apps: false, they store the returned token.
     */
    val cookieSession: Boolean = false,
)

@Serializable
enum class RoleDto { USER, ADMIN }

@Serializable
enum class AccountStatusDto { PENDING, APPROVED, REJECTED, REVOKED }

@Serializable
data class UserDto(
    val id: Long,
    val username: String,
    val email: String,
    val role: RoleDto,
    val status: AccountStatusDto,
)

/** [token] is for native apps (Authorization: Bearer); null for cookie sessions (web app). */
@Serializable
data class LoginResponseDto(val token: String? = null, val user: UserDto)

@Serializable
data class ManagedUserDto(
    val user: UserDto,
    val requestMessage: String? = null,
    val createdAtEpochMillis: Long,
)

@Serializable
data class TemporaryPasswordDto(val password: String)

@Serializable
data class ChangePasswordRequestDto(val currentPassword: String, val newPassword: String)
